import { ref, type Ref } from 'vue';

/**
 * Shared reactive state for PWA update availability.
 * Used across the app to show/hide the update banner.
 */
const updateAvailable: Ref<boolean> = ref(false);

/** Pending registration with a waiting service worker. */
let pendingRegistration: ServiceWorkerRegistration | null = null;

/**
 * Composable for PWA update detection and refresh.
 * Listens for service worker updates and exposes refresh action.
 */
export function usePwaUpdate() {
    function refresh() {
        if (pendingRegistration?.waiting) {
            pendingRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
            // Reload is triggered by controllerchange listener in registerPwaUpdates
            // If controllerchange doesn't fire (e.g. single tab), reload after a short delay
            setTimeout(() => window.location.reload(), 500);
        } else {
            window.location.reload();
        }
    }

    async function checkForUpdates(): Promise<boolean> {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
            await registration.update();
        }
        return true;
    }

    return {
        updateAvailable,
        refresh,
        checkForUpdates,
    };
}

/** Called by employee-portal.ts when a waiting SW is detected. */
export function setPwaUpdateAvailable(registration: ServiceWorkerRegistration) {
    updateAvailable.value = true;
    pendingRegistration = registration;
}
